<template>
    <div class="monthly-hydrology-chart">
        <chart-legend
            v-if="!pdf"
            :items="legendItems"
            :measurement-unit="options.tooltipMeasurementUnit"
            class="legend"
        />
        <data-by-unit-chart
            :data="getProcessedData()"
            :provided-options="options"
            class="chart"
        />
        <div v-if="pdf">
            <hr>
            <h4 class="title is-5 header">
                Legend
            </h4>
            <div class="pdf-legend">
                <chart-legend
                    :items="pdfLegendItems[0]"
                    :measurement-unit="options.tooltipMeasurementUnit"
                    :vertical="true"
                    class="pdf-legend-col"
                />
                <chart-legend
                    :items="pdfLegendItems[1]"
                    :measurement-unit="options.tooltipMeasurementUnit"
                    :vertical="true"
                    class="pdf-legend-col"
                />
                <chart-legend
                    :items="pdfLegendItems[2]"
                    :measurement-unit="options.tooltipMeasurementUnit"
                    :vertical="true"
                    class="pdf-legend-col"
                />
            </div>
        </div>
    </div>
</template>

<script>
import ChartLegend from '@foundryspatial/chart-legend';
import DataByUnitChart from '@foundryspatial/data-by-unit-chart';

import scssVars from '@/assets/scss/_variables.scss';

export default {
    components: {
        ChartLegend,
        DataByUnitChart,
    },

    props: {
        data: {
            type: Object,
            required: true,
        },

        providedOptions: {
            type: Object,
            required: false,
            default: () => ({}),
        },

        pdf: {
            type: Boolean,
            required: true,
        },
    },

    computed: {
        options() {
            return {
                tooltipMeasurementUnit: ' m³/s',
                padValueDomain: true,
            };
        },

        legendItems() {
            return [
                {
                    color: scssVars.colorRiskMgmtLevel3,
                    borderColor: '#000',
                    legendLabel: 'Risk Management 3',
                    labelOrder: 2,
                    legendType: 'box',
                },
                {
                    color: scssVars.colorRiskMgmtLevel2,
                    borderColor: '#000',
                    legendLabel: 'Risk Management 2',
                    labelOrder: 3,
                    legendType: 'box',
                },
                {
                    color: scssVars.colorRiskMgmtLevel1,
                    borderColor: '#000',
                    legendLabel: 'Risk Management 1',
                    labelOrder: 4,
                    legendType: 'box',
                },
                {
                    color: scssVars.colorMad,
                    legendLabel: 'MAD',
                    labelOrder: 5,
                    line: true,
                    legendValue: this.data.meanAnnualDischarge,
                    legendType: 'dashedLine',
                },
                {
                    color: scssVars.colorMad20,
                    legendLabel: 'MAD 20%',
                    labelOrder: 6,
                    line: true,
                    legendValue: this.data.meanAnnualDischarge * 0.2,
                    legendType: 'dashedLine',
                },
                {
                    color: scssVars.colorMad10,
                    legendLabel: 'MAD 10%',
                    labelOrder: 7,
                    line: true,
                    legendValue: this.data.meanAnnualDischarge * 0.1,
                    legendType: 'dashedLine',
                },
                {
                    color: scssVars.colorExistingAllocationsStroke,
                    borderColor: '#000',
                    legendLabel: 'Existing Allocations',
                    labelOrder: 1,
                    legendType: 'box',
                },
            ];
        },

        pdfLegendItems() {
            return [
                [
                    {
                        color: scssVars.colorExistingAllocationsStroke,
                        borderColor: '#000',
                        legendLabel: 'Existing Allocations',
                        labelOrder: 1,
                        legendType: 'box',
                    },
                ],
                [
                    {
                        color: scssVars.colorRiskMgmtLevel1,
                        borderColor: '#000',
                        legendLabel: 'Risk Management 1',
                        legendType: 'box',
                    },
                    {
                        color: scssVars.colorRiskMgmtLevel2,
                        borderColor: '#000',
                        legendLabel: 'Risk Management 2',
                        legendType: 'box',
                    },
                    {
                        color: scssVars.colorRiskMgmtLevel3,
                        borderColor: '#000',
                        legendLabel: 'Risk Management 3',
                        legendType: 'box',
                    },
                ],
                [
                    {
                        color: scssVars.colorMad10,
                        legendLabel: 'MAD 10%',
                        line: true,
                        legendValue: this.data.meanAnnualDischarge * 0.1,
                        legendType: 'dashedLine',
                    },
                    {
                        color: scssVars.colorMad20,
                        legendLabel: 'MAD 20%',
                        line: true,
                        legendValue: this.data.meanAnnualDischarge * 0.2,
                        legendType: 'dashedLine',
                    },
                    {
                        color: scssVars.colorMad,
                        legendLabel: 'MAD',
                        line: true,
                        legendValue: this.data.meanAnnualDischarge,
                        legendType: 'dashedLine',
                    },
                ],
            ];
        },
    },

    methods: {
        getProcessedData() {
            const {
                monthlyDischarge,
                rm1,
                rm2,
                rm3,
                existingAllocations,
            } = this.data;

            const mad = this.data.meanAnnualDischarge;

            const processedData = [];
            rm1.forEach((monthValue, monthIndex) => {
                const monthDischarge = monthlyDischarge[monthIndex];
                const monthRm1 = rm1[monthIndex];
                const monthRm2 = rm2[monthIndex];
                const monthRm3 = rm3[monthIndex];
                const monthEa = existingAllocations[monthIndex];

                processedData.push({
                    month: monthIndex + 1,
                    rm1: monthRm1,
                    rm1Max: monthDischarge,
                    rm1Min: monthDischarge - monthRm1,
                    rm2: monthRm2,
                    rm2Max: monthDischarge - monthRm1,
                    rm2Min: monthDischarge - monthRm2,
                    rm3: monthRm3,
                    rm3Max: monthDischarge - monthRm2,
                    rm3Min: 0,
                    mad,
                    mad20: mad * 0.2,
                    mad10: mad * 0.1,
                    ea: parseFloat(monthEa),
                    eaMax: monthDischarge,
                    eaMin: monthDischarge - monthEa > 0 ? monthDischarge - monthEa : 0,
                });
            });

            const sharedBandOptions = {
                styling: {
                    stroke: { value: 'black' },
                    strokeWidth: { value: 1 },
                },
            };

            const sharedLineOptions = {
                styling: {
                    strokeWidth: { value: 2 },
                    strokeDash: { value: [8, 4] },
                },
            };

            return [
                {
                    unit: 'month',
                    unitKey: 'month',
                    marks: [
                        {
                            type: 'band',
                            color: scssVars.colorRiskMgmtLevel3,
                            max: 'rm3Max',
                            min: 'rm3Min',
                            tooltipName: 'Risk Management 3',
                            tooltipValue: 'rm3',
                            labelOrder: 2,
                            ...sharedBandOptions,
                        },
                        {
                            type: 'band',
                            color: scssVars.colorRiskMgmtLevel2,
                            max: 'rm2Max',
                            min: 'rm2Min',
                            tooltipName: 'Risk Management 2',
                            tooltipValue: 'rm2',
                            labelOrder: 3,
                            ...sharedBandOptions,
                        },
                        {
                            type: 'band',
                            color: scssVars.colorRiskMgmtLevel1,
                            max: 'rm1Max',
                            min: 'rm1Min',
                            tooltipName: 'Risk Management 1',
                            tooltipValue: 'rm1',
                            labelOrder: 4,
                            ...sharedBandOptions,
                        },
                        {
                            type: 'line',
                            color: scssVars.colorMad,
                            value: 'mad',
                            valueName: 'MAD',
                            labelOrder: 5,
                            ...sharedLineOptions,
                        },
                        {
                            type: 'line',
                            color: scssVars.colorMad20,
                            value: 'mad20',
                            valueName: 'MAD 20%',
                            labelOrder: 6,
                            ...sharedLineOptions,
                        },
                        {
                            type: 'line',
                            color: scssVars.colorMad10,
                            value: 'mad10',
                            valueName: 'MAD 10%',
                            labelOrder: 7,
                            ...sharedLineOptions,
                        },
                        {
                            type: 'band',
                            color: scssVars.colorExistingAllocationsStroke,
                            max: 'eaMax',
                            min: 'eaMin',
                            tooltipValue: 'ea',
                            tooltipName: 'Existing Allocations',
                            labelOrder: 1,
                            ...sharedBandOptions,
                        },
                    ],

                    data: processedData,
                },
            ];
        },
    },
};
</script>
<style lang="scss">
.monthly-hydrology-chart {
    --legend-width: 15em;

    display: flex;
    flex-direction: row;

    .legend {
        width: var(--legend-width);
        align-self: flex-start;
        margin-right: 1em;

        .legend-item {
            width: 100%;
            margin-left: 0;
            margin-right: 0;

            .row {
                font-size: 0.8em;
                margin-left: 0;
            }
        }
    }

    .chart {
        width: calc(100% - var(--legend-width));
        height: 600px;
    }

    @media screen and (max-width: $desktop) {
        flex-direction: column;

        .legend {
            width: 100%;
            margin-left: 25px;

            .legend-item {
                flex: 1;
                align-self: center;
                max-width: 60%;
            }
        }

        .chart {
            width: 100%;
        }
    }

    @media screen and (max-width: $tablet) {
        .chart {
            height: 400px;
        }

        .legend {
            margin-left: 10px;

            .legend-item {
                margin: 0 1.5rem;
            }
        }
    }

    @media screen and (max-width: 350px) {
        .chart {
            height: 300px;
        }

        .legend {
            margin-left: -10px;
        }
    }
}
</style>
